/**
 * 按 pathname 解析主内容区页面；未注册路由使用占位页。
 */
import { useLocation } from 'react-router-dom';
import type { ComponentType } from 'react';
import { Dashboard } from '../pages/platform/Dashboard';
import { UsersListPage } from '../pages/platform/UsersListPage';
import { UserDetailPage } from '../pages/platform/UserDetailPage';
import { UserAdjustBalancePage } from '../pages/platform/UserAdjustBalancePage';
import { LedgerPage } from '../pages/platform/LedgerPage';
import { TransferLogPage } from '../pages/platform/TransferLogPage';
import { WithdrawLogPage } from '../pages/platform/WithdrawLogPage';
import { WithdrawReviewPage } from '../pages/platform/WithdrawReviewPage';
import { UserLevelsPage } from '../pages/platform/UserLevelsPage';
import { LevelUpgradePage } from '../pages/platform/LevelUpgradePage';
import { SplitLogPage } from '../pages/platform/SplitLogPage';
import { MentorshipLogPage } from '../pages/platform/MentorshipLogPage';
import { UocTransferQueuePage } from '../pages/platform/UocTransferQueuePage';
import { StaticRewardPreviewPage } from '../pages/platform/StaticRewardPreviewPage';
import { ForecastPage } from '../pages/platform/ForecastPage';
import { SmallMarketPerfPage } from '../pages/platform/SmallMarketPerfPage';
import { ContractPoolsPage } from '../pages/platform/ContractPoolsPage';
import { DynamicRewardOpsPage } from '../pages/platform/DynamicRewardOpsPage';
import { StagingRewardTestPage } from '../pages/platform/StagingRewardTestPage';
import { RewardDistributionPage } from '../pages/platform/RewardDistributionPage';
import { ReferralTreePage } from '../pages/platform/ReferralTreePage';
import { GenealogyChartPage } from '../pages/platform/GenealogyChartPage';
import { FreezeAccountPage } from '../pages/platform/FreezeAccountPage';
import { ShiftUplinePage } from '../pages/platform/ShiftUplinePage';
import { RechargeReviewPage } from '../pages/platform/RechargeReviewPage';
import { RechargeLogPage } from '../pages/platform/RechargeLogPage';
import { AnnouncementsPage } from '../pages/platform/AnnouncementsPage';
import { EpOrdersPage } from '../pages/ep/EpOrdersPage';
import { EpDisputesPage } from '../pages/ep/EpDisputesPage';
import { EpPricingTiersPage } from '../pages/ep/EpPricingTiersPage';
import { OtcOrdersPage } from '../pages/otc/OtcOrdersPage';
import { OtcTradesPage } from '../pages/otc/OtcTradesPage';
import { DonationCausesPage } from '../pages/donation/DonationCausesPage';
import { DonationLogPage } from '../pages/donation/DonationLogPage';
import { PccCardsPage } from '../pages/pcc/PccCardsPage';
import { PccHoldingsPage } from '../pages/pcc/PccHoldingsPage';
import { UocConfigPage } from '../pages/uoc/UocConfigPage';
import { UocLogPage } from '../pages/uoc/UocLogPage';
import { ActivationPoolPage } from '../pages/activation/ActivationPoolPage';
import { ActivationBatchPage } from '../pages/activation/ActivationBatchPage';
import { ParityUoActivationPage } from '../pages/activation/ParityUoActivationPage';
import { RechargeWalletPage } from '../pages/recharge-wallet/RechargeWalletPage';
import SubAccountPage from '../pages/sub-account/SubAccountPage';
import BotFlagPage from '../pages/bot-flag/BotFlagPage';
import {
  ReferralBonusLedgerPage,
  LevelBonusLedgerPage,
  NodeBonusLedgerPage,
  ForkLogLedgerPage,
} from '../pages/platform/LedgerDerivedPages';
import { ForkEventPage } from '../pages/platform/ForkEventPage';
import { StatsTokenPage } from '../pages/platform/StatsTokenPage';
import { StatsUserPage } from '../pages/platform/StatsUserPage';
import { SpReinvestWalletOrbPage } from '../pages/platform/SpReinvestWalletOrbPage';
import { SubAccountStatsPage } from '../pages/platform/SubAccountStatsPage';
import { OrbTop100Page } from '../pages/platform/OrbTop100Page';
import { OrbCapSimPage } from '../pages/platform/OrbCapSimPage';
import { TrendRegistrationsPage } from '../pages/platform/TrendRegistrationsPage';
import { TrendActivePage } from '../pages/platform/TrendActivePage';
import { BlacklistPage } from '../pages/platform/BlacklistPage';
import { WhitelistPage } from '../pages/platform/WhitelistPage';
import { KycReviewPage } from '../pages/platform/KycReviewPage';
import KycSubmissionsPage from '../pages/kyc/KycSubmissionsPage';
import UploadLogPage from '../pages/upload/UploadLogPage';
import UnitPricePage from '../pages/unit-price/UnitPricePage';
import UserOpSnapshotPage from '../pages/user-op-snapshot/UserOpSnapshotPage';
import HonorJourneyPage from '../pages/honor/HonorJourneyPage';
import TransferCoinPage from '../pages/transfer-coin/TransferCoinPage';
import { ParityUoUserPage } from '../pages/parity/ParityUoUserPage';
import { ParityUoUocPage } from '../pages/parity/ParityUoUocPage';
import FpStatsPage from '../pages/fp-stats/FpStatsPage';
import MarketCapPage from '../pages/market-cap/MarketCapPage';
import DeepSearchPage from '../pages/deep-search/DeepSearchPage';
import BotBatchPage from '../pages/bot-batch/BotBatchPage';
import { BannerPage } from '../pages/platform/BannerPage';
import { HelpPage } from '../pages/platform/HelpPage';
import { FaqPage } from '../pages/platform/FaqPage';
import { TicketPage } from '../pages/platform/TicketPage';
import { ActivityPage } from '../pages/platform/ActivityPage';
import { TaskPage } from '../pages/platform/TaskPage';
import { PushPage } from '../pages/platform/PushPage';
import { AdminsPage } from '../pages/system/AdminsPage';
import { RolesPage } from '../pages/system/RolesPage';
import { MenusPage } from '../pages/system/MenusPage';
import { NodesPage } from '../pages/system/NodesPage';
import { SystemConfigPage } from '../pages/system/SystemConfigPage';
import { OpLogPage } from '../pages/system/OpLogPage';
import { BackupPage } from '../pages/system/BackupPage';
import { CachePage } from '../pages/system/CachePage';
import { PhpMirrorPage } from '../pages/system/PhpMirrorPage';
import { PlaceholderPage } from '../pages/common/PlaceholderPage';
import { PhpReplicaPage } from '../pages/parity/PhpReplicaPage';
import {
  PhpUserListParityPage,
  PhpUserOperationParityPage,
} from '../pages/parity/PhpUserMgmtPage';
import { ParityUserSearchPage } from '../pages/parity/ParityUserSearchPage';
import {
  ParityPrivateLetterPage,
  ParityOpinionPage,
  ParityOpsSnapshotPage,
  ParityBonusTradeLogPage,
  ParityPiSettlementPage,
  ParityAccountConfigLogPage,
  ParityUoCountReportPage,
  ParityUocMintLogPage,
  ParityUocWithdrawLogPage,
  ParityUploadManagePage,
} from '../pages/platform/ParityPages';

const REGISTRY: Record<string, ComponentType> = {
  '/platform/dashboard': Dashboard,
  '/platform/users': UsersListPage,
  '/platform/user/detail': UserDetailPage,
  '/platform/user/adjust': UserAdjustBalancePage,
  '/platform/ledger': LedgerPage,
  '/platform/transfer/log': TransferLogPage,
  '/platform/withdraw/review': WithdrawReviewPage,
  '/platform/withdraw/log': WithdrawLogPage,
  '/platform/level': UserLevelsPage,
  '/platform/level/upgrade': LevelUpgradePage,
  '/platform/split/log': SplitLogPage,
  '/platform/mentorship/log': MentorshipLogPage,
  '/platform/uoc/transfer-queue': UocTransferQueuePage,
  '/platform/static-reward/preview': StaticRewardPreviewPage,
  '/platform/forecast': ForecastPage,
  '/platform/small-market-perf': SmallMarketPerfPage,
  '/platform/contract-pools': ContractPoolsPage,
  '/platform/dynamic-rewards/ops': DynamicRewardOpsPage,
  '/platform/staging-reward-test': StagingRewardTestPage,
  '/platform/reward-distributions': RewardDistributionPage,
  '/platform/user/tree': ReferralTreePage,
  '/platform/binary_tree': GenealogyChartPage,
  '/platform/user/freeze': FreezeAccountPage,
  '/platform/user/delete': FreezeAccountPage,
  '/platform/user/shift-upline': ShiftUplinePage,
  '/platform/recharge': RechargeReviewPage,
  '/platform/recharge/log': RechargeLogPage,
  '/platform/announcement': AnnouncementsPage,
  '/ep/orders': EpOrdersPage,
  '/ep/disputes': EpDisputesPage,
  '/ep/pricing': EpPricingTiersPage,
  '/otc/orders': OtcOrdersPage,
  '/otc/trades': OtcTradesPage,
  '/donation/causes': DonationCausesPage,
  '/donation/log': DonationLogPage,
  '/pcc/cards': PccCardsPage,
  '/pcc/holdings': PccHoldingsPage,
  '/uoc/config': UocConfigPage,
  '/uoc/recharge': UocConfigPage,  // recharge is part of config page
  '/uoc/log': UocLogPage,
  '/activation/pool': ActivationPoolPage,
  '/activation/distribute': ActivationPoolPage,  // distribute is part of pool page
  '/activation/batch': ActivationBatchPage,
  '/recharge_wallet/config': RechargeWalletPage,
  '/sub_account/manage': SubAccountPage,
  '/bot_flag/manage': BotFlagPage,
  '/platform/referral/bonus': ReferralBonusLedgerPage,
  '/platform/level/bonus': LevelBonusLedgerPage,
  '/platform/node/bonus': NodeBonusLedgerPage,
  '/platform/fork/event': ForkEventPage,
  '/platform/fork/log': ForkLogLedgerPage,
  '/platform/token/stats': StatsTokenPage,
  '/platform/user/stats': StatsUserPage,
  '/platform/sp-reinvest-wallet-orb': SpReinvestWalletOrbPage,
  '/platform/sub-account-stats': SubAccountStatsPage,
  '/platform/orb-top100': OrbTop100Page,
  '/platform/orb-cap': OrbCapSimPage,
  '/platform/trend/new': TrendRegistrationsPage,
  '/platform/trend/active': TrendActivePage,
  '/platform/kyc': KycReviewPage,
  '/kyc/submissions': KycSubmissionsPage,
  '/upload/log': UploadLogPage,
  '/unit_price/config': UnitPricePage,
  '/user_op/snapshot': UserOpSnapshotPage,
  '/honor/journey': HonorJourneyPage,
  '/platform/transfer_coin': TransferCoinPage,
  '/platform/fp_stats': FpStatsPage,
  '/platform/market_cap': MarketCapPage,
  '/platform/deep_search': DeepSearchPage,
  '/bot_flag/batch': BotBatchPage,
  '/platform/blacklist': BlacklistPage,
  '/platform/whitelist': WhitelistPage,
  '/platform/banner': BannerPage,
  '/platform/help': HelpPage,
  '/platform/faq': FaqPage,
  '/platform/ticket': TicketPage,
  '/platform/activity': ActivityPage,
  '/platform/task': TaskPage,
  '/platform/push': PushPage,
  '/system/oplog': OpLogPage,
  '/system/backup': BackupPage,
  '/system/cache': CachePage,
  '/system/admins': AdminsPage,
  '/system/role': RolesPage,
  '/system/menu': MenusPage,
  '/system/node': NodesPage,
  '/system/config': SystemConfigPage,
  // 旧 PHP 后台有、Node 端真实数据接入（/api/admin/parity/*）
  '/platform/private-letter': ParityPrivateLetterPage,
  '/platform/opinion': ParityOpinionPage,
  '/system/upload-manage': ParityUploadManagePage,
  '/platform/ops-snapshot': ParityOpsSnapshotPage,
  '/platform/bonus-trade-log': ParityBonusTradeLogPage,
  '/platform/pi-settlement': ParityPiSettlementPage,
  '/platform/account-config-log': ParityAccountConfigLogPage,
  '/platform/uo-count-report': ParityUoCountReportPage,
  '/platform/uoc-mint-log': ParityUocMintLogPage,
  '/platform/uoc-withdraw-log': ParityUocWithdrawLogPage,
  '/system/php-mirror': PhpMirrorPage,
};

export function DynamicAdminPage() {
  const { pathname } = useLocation();
  // `/parity/user_user` and `/parity/user_useroperation` share the same
  // `php_user_user` mirror table, so we render them through a purpose-built
  // user-management page (pinned identity/action cols, balance popover,
  // freeze/EP-limit/blood modals). The generic spec-driven PhpReplicaPage
  // either lost native 编辑/删除 buttons (user_user, because the overlay
  // tripped the hasOperat check) or interleaved tool cols mid-row
  // (user_useroperation) — see PhpUserMgmtPage.tsx header for details.
  if (pathname === '/parity/user_user') return <PhpUserListParityPage />;
  if (pathname === '/parity/user_useroperation') return <PhpUserOperationParityPage />;
  if (pathname === '/parity/uo_usersearch') return <ParityUserSearchPage />;
  // 线网 uo.config/index 为「TP 手续费 + 拆分」表单，不是 php_user 列表；spec 误把 index 指到 uo.config/user
  if (pathname === '/parity/uo_config') return <SplitLogPage />;
  // 线网 uo.Activation 为「激活码转TP/EP/RP」表单；非英文激活码池页。数据见 GET .../activation/legacy-php-form
  if (pathname === '/parity/uo_activation') return <ParityUoActivationPage />;
  // 线网 uo.user/index：两 tab（activation_to / uoc_to）+ uo.user/log；见 ParityUoUserPage
  if (pathname === '/parity/uo_user') return <ParityUoUserPage />;
  // 线网 uo.uoc 为 UOC 转 RP/TP/EP 单表单，非 user 表；见 ParityUoUocPage
  if (pathname === '/parity/uo_uoc') return <ParityUoUocPage />;
  // /parity/<slug> renders an Ant table that mirrors the legacy PHP page
  // 1:1 (cols + filters + toolbar) using `php_*` mirror data — see
  //   packages/admin/src/pages/parity/PhpReplicaPage.tsx
  //   packages/admin/src/config/php-page-specs.generated.json
  if (pathname.startsWith('/parity/')) return <PhpReplicaPage pathname={pathname} />;
  const C = REGISTRY[pathname] ?? PlaceholderPage;
  return <C />;
}
